import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/api";
import { PageHeader } from "@/pages/admin/AdminDashboard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Edit3, Trash2, ClipboardList, ListChecks, MapPin, Clock, User as UserIcon, CheckCircle2, PlayCircle, XCircle, AlertTriangle, Wrench, Search, Camera } from "lucide-react";
import { AuthImage } from "@/components/AuthImage";

const STATUS_META = {
  planned: { label: "Planifiée", color: "#0EA5E9", icon: Clock, bg: "bg-sky-50 text-sky-700 border-sky-200" },
  in_progress: { label: "En cours", color: "#D97706", icon: PlayCircle, bg: "bg-amber-50 text-amber-700 border-amber-200" },
  completed: { label: "Terminée", color: "#059669", icon: CheckCircle2, bg: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  cancelled: { label: "Annulée", color: "#6B7280", icon: XCircle, bg: "bg-gray-50 text-gray-600 border-gray-200" },
};

const PRIORITY_META = {
  low: { label: "Faible", color: "#059669" },
  medium: { label: "Moyenne", color: "#D97706" },
  high: { label: "Haute", color: "#E60000" },
  urgent: { label: "Urgente", color: "#7C2D12" },
};

const TYPE_META = {
  inspection: "Inspection",
  maintenance: "Maintenance",
  audit: "Audit",
  installation: "Installation",
  depannage: "Dépannage",
  livraison: "Livraison",
  autre: "Autre",
};

function empty() {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(9, 0, 0, 0);
  return {
    title: "",
    description: "",
    type: "inspection",
    priority: "medium",
    scheduled_at: d.toISOString().slice(0, 16),
    duration_minutes: 60,
    site_label: "",
    address: "",
    assignee_id: "",
    notes: "",
    checklist_text: "",
  };
}

export default function ChefQualiParcInterventions() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ by_status: {}, total: 0 });
  const [salaries, setSalaries] = useState([]);
  const [filter, setFilter] = useState({ status: "all", priority: "all", q: "" });
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty());
  const [detail, setDetail] = useState(null);

  const load = async () => {
    try {
      const [a, b, c] = await Promise.all([
        api.get("/chef/qualiparc/interventions"),
        api.get("/chef/qualiparc/interventions/stats/summary"),
        api.get("/chef/salaries"),
      ]);
      setItems(a.data || []);
      setStats(b.data || { by_status: {}, total: 0 });
      setSalaries(c.data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = filter.q.trim().toLowerCase();
    return items.filter((it) => {
      if (filter.status !== "all" && it.status !== filter.status) return false;
      if (filter.priority !== "all" && it.priority !== filter.priority) return false;
      if (q && !(`${it.title} ${it.address} ${it.site_label} ${it.assignee_name || ""}`).toLowerCase().includes(q)) return false;
      return true;
    });
  }, [items, filter]);

  const openNew = () => { setEditing(null); setForm(empty()); setOpen(true); };
  const openEdit = (it) => {
    setEditing(it);
    setForm({
      title: it.title || "",
      description: it.description || "",
      type: it.type || "inspection",
      priority: it.priority || "medium",
      scheduled_at: (it.scheduled_at || "").slice(0, 16),
      duration_minutes: it.duration_minutes || 60,
      site_label: it.site_label || "",
      address: it.address || "",
      assignee_id: it.assignee_id || "",
      notes: it.notes || "",
      checklist_text: (it.checklist || []).map((c) => c.label).join("\n"),
    });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const checklist = (form.checklist_text || "").split("\n").map((s) => s.trim()).filter(Boolean).map((label) => ({ label }));
      const payload = {
        title: form.title.trim(),
        description: form.description,
        type: form.type,
        priority: form.priority,
        scheduled_at: new Date(form.scheduled_at).toISOString(),
        duration_minutes: Number(form.duration_minutes) || 60,
        site_label: form.site_label || null,
        address: form.address || null,
        assignee_id: form.assignee_id || null,
        notes: form.notes || null,
        checklist,
      };
      if (editing) {
        await api.patch(`/chef/qualiparc/interventions/${editing.id}`, payload);
        toast.success("Intervention mise à jour");
      } else {
        await api.post("/chef/qualiparc/interventions", payload);
        toast.success("Intervention créée");
      }
      setOpen(false); setEditing(null); setForm(empty());
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const changeStatus = async (id, status) => {
    try {
      await api.patch(`/chef/qualiparc/interventions/${id}`, { status });
      toast.success("Statut mis à jour");
      load();
      if (detail?.id === id) setDetail({ ...detail, status });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette intervention ?")) return;
    try { await api.delete(`/chef/qualiparc/interventions/${id}`); toast.success("Supprimée"); load(); if (detail?.id === id) setDetail(null); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const toggleChecklist = async (interv, idx) => {
    const cl = [...(interv.checklist || [])];
    cl[idx] = { ...cl[idx], done: !cl[idx].done };
    try {
      const { data } = await api.patch(`/chef/qualiparc/interventions/${interv.id}`, { checklist: cl });
      setItems((prev) => prev.map((it) => (it.id === data.id ? data : it)));
      if (detail?.id === data.id) setDetail(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="min-h-full">
      <PageHeader
        eyebrow="QualiParc"
        title="Interventions"
        sub="Planifiez, suivez et clôturez les interventions terrain de vos équipes."
        right={
          <Button data-testid="qp-new-intervention-btn" onClick={openNew} className="h-10 bg-[#002FA7] hover:bg-[#0A0A0A] text-white text-xs uppercase tracking-[0.2em] font-bold rounded-none">
            <Plus className="h-4 w-4 mr-2" /> Nouvelle intervention
          </Button>
        }
      />

      <div className="p-6 lg:p-10 grid grid-cols-2 lg:grid-cols-5 gap-3">
        <KPI label="Total" value={stats.total} color="#0A0A0A" testid="qp-stat-total" />
        <KPI label="Planifiées" value={stats.by_status?.planned ?? 0} color="#0EA5E9" testid="qp-stat-planned" />
        <KPI label="En cours" value={stats.by_status?.in_progress ?? 0} color="#D97706" testid="qp-stat-progress" />
        <KPI label="Terminées" value={stats.by_status?.completed ?? 0} color="#059669" testid="qp-stat-completed" />
        <KPI label="Annulées" value={stats.by_status?.cancelled ?? 0} color="#6B7280" testid="qp-stat-cancelled" />
      </div>

      <div className="px-6 lg:px-10 pb-4 flex flex-wrap gap-3 items-end">
        <div>
          <Label className="text-xs">Statut</Label>
          <Select value={filter.status} onValueChange={(v) => setFilter((f) => ({ ...f, status: v }))}>
            <SelectTrigger className="w-40 h-9" data-testid="qp-filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous</SelectItem>
              {Object.entries(STATUS_META).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Priorité</Label>
          <Select value={filter.priority} onValueChange={(v) => setFilter((f) => ({ ...f, priority: v }))}>
            <SelectTrigger className="w-40 h-9" data-testid="qp-filter-priority"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes</SelectItem>
              {Object.entries(PRIORITY_META).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1 min-w-[240px]">
          <Label className="text-xs">Recherche</Label>
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#868E96]" />
            <Input data-testid="qp-filter-search" placeholder="Titre, adresse, technicien..." value={filter.q} onChange={(e) => setFilter((f) => ({ ...f, q: e.target.value }))} className="pl-7 h-9" />
          </div>
        </div>
      </div>

      <div className="px-6 lg:px-10 pb-10">
        {filtered.length === 0 ? (
          <div className="border border-[#DEE2E6] bg-white p-10 text-center text-sm text-[#868E96]" data-testid="qp-empty">
            <ClipboardList className="h-8 w-8 mx-auto mb-2 opacity-40" />
            Aucune intervention. Cliquez sur "Nouvelle intervention" pour démarrer.
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {filtered.map((it) => {
              const st = STATUS_META[it.status] || STATUS_META.planned;
              const pr = PRIORITY_META[it.priority] || PRIORITY_META.medium;
              const StIcon = st.icon;
              const clDone = (it.checklist || []).filter((c) => c.done).length;
              const clTotal = (it.checklist || []).length;
              return (
                <div key={it.id} data-testid={`qp-card-${it.id}`} className="border border-[#DEE2E6] bg-white p-4 hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 border font-bold ${st.bg}`}>
                          <StIcon className="h-3 w-3 inline mr-1" /> {st.label}
                        </span>
                        <span className="text-[10px] uppercase tracking-[0.15em] font-bold" style={{ color: pr.color }}>● {pr.label}</span>
                        <span className="text-[10px] uppercase text-[#868E96] tracking-[0.15em]">{TYPE_META[it.type]}</span>
                      </div>
                      <h3 className="font-display font-bold text-base mt-2 truncate">{it.title}</h3>
                      {it.description && <p className="text-xs text-[#495057] mt-1 line-clamp-2">{it.description}</p>}
                    </div>
                    <div className="flex gap-1">
                      <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => openEdit(it)} data-testid={`qp-edit-${it.id}`}><Edit3 className="h-3.5 w-3.5" /></Button>
                      <Button size="icon" variant="ghost" className="h-8 w-8 text-red-600 hover:text-red-700" onClick={() => remove(it.id)} data-testid={`qp-delete-${it.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                    </div>
                  </div>
                  <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                    <div className="flex items-center gap-1.5 text-[#495057]"><Clock className="h-3 w-3" /> {new Date(it.scheduled_at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}</div>
                    <div className="flex items-center gap-1.5 text-[#495057]"><UserIcon className="h-3 w-3" /> {it.assignee_name || "Non assignée"}</div>
                    {(it.site_label || it.address) && <div className="flex items-center gap-1.5 text-[#495057] col-span-2 truncate"><MapPin className="h-3 w-3" /> {[it.site_label, it.address].filter(Boolean).join(" — ")}</div>}
                  </div>
                  {clTotal > 0 && (
                    <div className="mt-3 border-t border-[#F1F3F5] pt-3">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-[#868E96] font-semibold mb-1.5">Checklist ({clDone}/{clTotal})</div>
                      <div className="w-full bg-[#F1F3F5] h-1.5">
                        <div className="bg-[#059669] h-1.5" style={{ width: `${clTotal ? Math.round((clDone / clTotal) * 100) : 0}%` }} />
                      </div>
                    </div>
                  )}
                  <div className="mt-3 flex gap-2 flex-wrap">
                    {it.status !== "in_progress" && it.status !== "completed" && (
                      <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => changeStatus(it.id, "in_progress")} data-testid={`qp-start-${it.id}`}>
                        <PlayCircle className="h-3 w-3 mr-1" /> Démarrer
                      </Button>
                    )}
                    {it.status !== "completed" && it.status !== "cancelled" && (
                      <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => changeStatus(it.id, "completed")} data-testid={`qp-complete-${it.id}`}>
                        <CheckCircle2 className="h-3 w-3 mr-1" /> Terminer
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={() => setDetail(it)} data-testid={`qp-open-${it.id}`}>Détails →</Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Create/Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editing ? "Modifier l'intervention" : "Nouvelle intervention"}</DialogTitle>
            <DialogDescription>Planification, assignation, checklist et détails terrain.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div>
              <Label className="text-xs">Titre *</Label>
              <Input data-testid="qp-form-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Type</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger data-testid="qp-form-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(TYPE_META).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Priorité</Label>
                <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                  <SelectTrigger data-testid="qp-form-priority"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(PRIORITY_META).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Durée (min)</Label>
                <Input type="number" min={0} value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Date & heure *</Label>
                <Input type="datetime-local" data-testid="qp-form-datetime" value={form.scheduled_at} onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })} required />
              </div>
              <div>
                <Label className="text-xs">Technicien assigné</Label>
                <Select value={form.assignee_id || "none"} onValueChange={(v) => setForm({ ...form, assignee_id: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="qp-form-assignee"><SelectValue placeholder="Aucun" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Non assignée</SelectItem>
                    {salaries.map((s) => <SelectItem key={s.id} value={s.id}>{s.prenom} {s.nom} ({s.code})</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Site / lieu</Label>
                <Input value={form.site_label} onChange={(e) => setForm({ ...form, site_label: e.target.value })} placeholder="Ex : Agence Nord" />
              </div>
              <div>
                <Label className="text-xs">Adresse</Label>
                <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="12 rue de la Paix, 75002" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Description</Label>
              <Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs">Checklist (une ligne = un item)</Label>
              <Textarea rows={4} data-testid="qp-form-checklist" value={form.checklist_text} onChange={(e) => setForm({ ...form, checklist_text: e.target.value })} placeholder={"Vérifier l'installation\nPhotographier l'existant\nFaire signer le PV"} />
            </div>
            <div>
              <Label className="text-xs">Notes internes</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
              <Button type="submit" data-testid="qp-form-submit" className="bg-[#002FA7]">{editing ? "Enregistrer" : "Créer"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Detail dialog */}
      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="max-w-2xl">
          {detail && (() => {
            const st = STATUS_META[detail.status] || STATUS_META.planned;
            return (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <Wrench className="h-4 w-4" /> {detail.title}
                  </DialogTitle>
                  <DialogDescription>
                    <span className={`text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 border font-bold ${st.bg}`}>{st.label}</span>
                    <span className="text-[10px] uppercase text-[#868E96] ml-2">{TYPE_META[detail.type]}</span>
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-3 text-sm">
                  {detail.description && <p className="text-[#495057]">{detail.description}</p>}
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <Info label="Date"><Clock className="h-3 w-3 inline mr-1" />{new Date(detail.scheduled_at).toLocaleString("fr-FR")}</Info>
                    <Info label="Durée">{detail.duration_minutes} min</Info>
                    <Info label="Technicien">{detail.assignee_name || "—"}</Info>
                    <Info label="Site">{detail.site_label || "—"}</Info>
                    <Info label="Adresse">{detail.address || "—"}</Info>
                    <Info label="Priorité">{PRIORITY_META[detail.priority]?.label}</Info>
                  </div>
                  {(detail.checklist || []).length > 0 && (
                    <div>
                      <div className="text-xs uppercase tracking-[0.15em] text-[#868E96] font-semibold mb-2">Checklist</div>
                      <div className="space-y-1">
                        {(detail.checklist || []).map((c, idx) => (
                          <button
                            key={c.id || idx}
                            type="button"
                            data-testid={`qp-detail-cl-${idx}`}
                            onClick={() => toggleChecklist(detail, idx)}
                            className={`w-full text-left flex items-center gap-2 p-2 border ${c.done ? "border-emerald-300 bg-emerald-50" : "border-[#DEE2E6] bg-white hover:bg-[#F8F9FA]"}`}
                          >
                            <CheckCircle2 className={`h-4 w-4 ${c.done ? "text-emerald-600" : "text-[#DEE2E6]"}`} />
                            <span className={`text-sm ${c.done ? "line-through text-[#868E96]" : ""}`}>{c.label}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  {detail.notes && (
                    <div>
                      <div className="text-xs uppercase tracking-[0.15em] text-[#868E96] font-semibold mb-1">Notes internes</div>
                      <div className="p-2 border border-[#DEE2E6] bg-[#F8F9FA] text-xs">{detail.notes}</div>
                    </div>
                  )}
                  {(detail.photos || []).length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-[#868E96] font-semibold mb-2">
                        <Camera className="h-3 w-3" /> Photos terrain ({detail.photos.length})
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        {detail.photos.map((p) => (
                          <div key={p.id} className="relative aspect-square bg-[#F1F3F5] border border-[#DEE2E6] overflow-hidden" data-testid={`chef-photo-${p.id}`}>
                            <AuthImage path={`/qualiparc/interventions/${detail.id}/photos/${p.id}`} className="w-full h-full object-cover" />
                            <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[9px] px-1 py-0.5 truncate">{p.uploaded_by_name}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <DialogFooter>
                  {detail.status !== "cancelled" && detail.status !== "completed" && (
                    <Button variant="outline" onClick={() => changeStatus(detail.id, "cancelled")} data-testid="qp-detail-cancel"><XCircle className="h-3.5 w-3.5 mr-1" /> Annuler</Button>
                  )}
                  <Button variant="outline" onClick={() => { setDetail(null); openEdit(detail); }}><Edit3 className="h-3.5 w-3.5 mr-1" /> Modifier</Button>
                </DialogFooter>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function KPI({ label, value, color, testid }) {
  return (
    <div className="border border-[#DEE2E6] bg-white p-4" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.2em] text-[#868E96] font-semibold">{label}</div>
      <div className="mt-1 font-display font-black text-2xl" style={{ color }}>{value}</div>
    </div>
  );
}

function Info({ label, children }) {
  return (
    <div className="p-2 border border-[#DEE2E6] bg-[#F8F9FA]">
      <div className="text-[10px] uppercase tracking-[0.15em] text-[#868E96] font-semibold">{label}</div>
      <div className="text-sm text-[#0A0A0A] mt-0.5">{children}</div>
    </div>
  );
}
